import { useEffect, useMemo, useState } from 'react';
import { Check, X } from 'lucide-react';
import { cloneExploreFilters, countActiveExploreFilters } from '@utils/exploreFilters';

/**
 * Category config — add entries here to extend the sheet.
 * `id` must be unique; `sidebarLabel` in the left nav; `description` above options on the right.
 */
export const EXPLORE_FILTER_CATEGORY_ORDER = [
  { id: 'access', sidebarLabel: 'Who can join', description: 'Who is welcome at listed vibes' },
  { id: 'verification', sidebarLabel: 'Verification', description: 'Face-verified hosts or Any' },
  { id: 'age', sidebarLabel: 'Age group', description: 'Approximate age of organizers' },
  { id: 'date', sidebarLabel: 'When', description: 'When vibes happen' },
  { id: 'distance', sidebarLabel: 'Distance', description: 'How far from you to search' },
  { id: 'availability', sidebarLabel: 'Spots available', description: 'Spots left' },
];

const optionRow = (selected) =>
  [
    'flex w-full items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left text-[13px] font-medium leading-snug text-surface-700 transition-colors duration-150 active:bg-surface-50 dark:text-surface-200 dark:active:bg-surface-800/50',
    selected
      ? 'border-brand-200/90 bg-brand-50/60 text-surface-900 dark:border-brand-500/25 dark:bg-brand-500/[0.08] dark:text-surface-50'
      : 'border-surface-100 bg-white hover:border-surface-200 hover:bg-surface-50/80 dark:border-surface-800 dark:bg-surface-900 dark:hover:border-surface-700 dark:hover:bg-surface-800/40',
  ].join(' ');

const OptionRow = ({ selected, onClick, children }) => (
  <button type="button" className={optionRow(selected)} onClick={onClick}>
    <span
      className={[
        'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border text-[9px]',
        selected
          ? 'border-brand-400/70 bg-brand-500/90 text-white dark:border-brand-400/50 dark:bg-brand-500/80'
          : 'border-surface-200/90 bg-transparent dark:border-surface-600',
      ].join(' ')}
      aria-hidden
    >
      {selected ? <Check size={10} strokeWidth={2.5} /> : null}
    </span>
    <span className="min-w-0 flex-1">{children}</span>
  </button>
);

const ExploreFilterSheet = ({ open, appliedFilters, onClose, onApply, onResetAll }) => {
  const [draft, setDraft] = useState(() => cloneExploreFilters(appliedFilters));
  const [activeCategory, setActiveCategory] = useState(EXPLORE_FILTER_CATEGORY_ORDER[0].id);

  useEffect(() => {
    if (open) {
      setDraft(cloneExploreFilters(appliedFilters));
      setActiveCategory(EXPLORE_FILTER_CATEGORY_ORDER[0].id);
    }
  }, [open, appliedFilters]);

  const draftCount = useMemo(() => countActiveExploreFilters(draft), [draft]);

  if (!open) return null;

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const handleReset = () => {
    onResetAll();
    setDraft(cloneExploreFilters({}));
  };

  const handleApply = () => {
    onApply(cloneExploreFilters(draft));
    onClose();
  };

  const renderOptions = () => {
    switch (activeCategory) {
      case 'access':
        return (
          <div className="flex flex-col gap-1.5">
            {[
              { id: 'all', label: 'Any' },
              { id: 'everyone', label: 'Open to all' },
              { id: 'female', label: 'Girls only' },
              { id: 'male', label: 'Boys only' },
            ].map((o) => (
              <OptionRow
                key={o.id}
                selected={draft.access === o.id}
                onClick={() => {
                  if (o.id !== 'all' && draft.access === o.id) set({ access: 'all' });
                  else set({ access: o.id });
                }}
              >
                {o.label}
              </OptionRow>
            ))}
          </div>
        );
      case 'verification':
        return (
          <div className="flex flex-col gap-1.5">
            {[
              { id: 'all', label: 'All' },
              { id: 'verified', label: 'Verified only' },
              { id: 'nonVerified', label: 'Non-verified only' },
            ].map((o) => (
              <OptionRow
                key={o.id}
                selected={draft.verification === o.id}
                onClick={() => {
                  if (o.id !== 'all' && draft.verification === o.id) set({ verification: 'all' });
                  else set({ verification: o.id });
                }}
              >
                {o.label}
              </OptionRow>
            ))}
          </div>
        );
      case 'age':
        return (
          <div className="flex flex-col gap-1.5">
            {[
              { id: 'all', label: 'Any' },
              { id: 'teens', label: '13–17' },
              { id: 'youngAdults', label: '18–24' },
              { id: 'adults', label: '25–34' },
              { id: 'mature', label: '35+' },
            ].map((o) => (
              <OptionRow
                key={o.id}
                selected={draft.ageGroup === o.id}
                onClick={() => {
                  if (o.id !== 'all' && draft.ageGroup === o.id) set({ ageGroup: 'all' });
                  else set({ ageGroup: o.id });
                }}
              >
                {o.label}
              </OptionRow>
            ))}
          </div>
        );
      case 'date':
        return (
          <div className="flex flex-col gap-1.5">
            {[
              { id: 'none', label: 'Any' },
              { id: 'today', label: "Today's Vibes" },
              { id: 'tomorrow', label: 'Tomorrow' },
              { id: 'weekend', label: 'Weekend Vibes' },
              { id: 'custom', label: 'Pick a date' },
            ].map((o) => (
              <OptionRow
                key={o.id}
                selected={o.id === 'none' ? draft.dateMode === 'none' || !draft.dateMode : draft.dateMode === o.id}
                onClick={() => {
                  const mode = draft.dateMode || 'none';
                  if (o.id === 'none') {
                    set({ dateMode: 'none', customDateYmd: '' });
                  } else if (mode === o.id) {
                    set({ dateMode: 'none', customDateYmd: '' });
                  } else {
                    set({ dateMode: o.id, ...(o.id !== 'custom' ? { customDateYmd: '' } : {}) });
                  }
                }}
              >
                {o.label}
              </OptionRow>
            ))}
            {draft.dateMode === 'custom' && (
              <label className="mt-1 block rounded-xl border border-surface-200/90 bg-surface-50/80 p-3 dark:border-surface-700 dark:bg-surface-800/60">
                <span className="mb-1.5 block text-[10px] font-medium uppercase tracking-[0.06em] text-surface-400 dark:text-surface-500">
                  Choose date
                </span>
                <input
                  type="date"
                  value={draft.customDateYmd || ''}
                  onChange={(e) => set({ customDateYmd: e.target.value })}
                  className="w-full rounded-lg border border-surface-200 bg-white px-3 py-2.5 text-sm dark:border-surface-600 dark:bg-surface-900"
                />
              </label>
            )}
          </div>
        );
      case 'distance':
        return (
          <div className="flex flex-col gap-1.5">
            {[
              { preset: 'any', label: 'Any' },
              { preset: '2', label: 'Within 2 km' },
              { preset: '5', label: 'Within 5 km' },
              { preset: '10', label: 'Within 10 km' },
              { preset: 'custom', label: 'Custom radius' },
            ].map((o) => (
              <OptionRow
                key={o.preset}
                selected={draft.distancePreset === o.preset}
                onClick={() => {
                  if (o.preset === 'any') {
                    set({ distancePreset: 'any', distanceKm: 50 });
                  } else if (o.preset === 'custom') {
                    if (draft.distancePreset === 'custom') {
                      set({ distancePreset: 'any', distanceKm: 50 });
                    } else {
                      const km = Number(draft.distanceKm);
                      const nextKm =
                        Number.isFinite(km) && km >= 1 && km <= 49 ? Math.round(km) : 15;
                      set({ distancePreset: 'custom', distanceKm: nextKm });
                    }
                  } else if (draft.distancePreset === o.preset) {
                    set({ distancePreset: 'any', distanceKm: 50 });
                  } else {
                    const n = o.preset === '2' ? 2 : o.preset === '5' ? 5 : 10;
                    set({ distancePreset: o.preset, distanceKm: n });
                  }
                }}
              >
                {o.label}
              </OptionRow>
            ))}
            {draft.distancePreset === 'custom' && (
              <div className="mt-1 rounded-xl border border-surface-200/90 bg-surface-50/80 p-3 dark:border-surface-700 dark:bg-surface-800/60">
                <label className="mb-2 flex items-center justify-between text-[11px] font-medium text-surface-600 dark:text-surface-300">
                  <span>Radius</span>
                  <span className="tabular-nums text-brand-600 dark:text-brand-400">
                    {Math.min(49, Math.max(1, Math.round(Number(draft.distanceKm)) || 15))} km
                  </span>
                </label>
                <input
                  type="range"
                  min={1}
                  max={49}
                  step={1}
                  value={Math.min(49, Math.max(1, Math.round(Number(draft.distanceKm)) || 15))}
                  onChange={(e) =>
                    set({
                      distanceKm: parseInt(e.target.value, 10),
                      distancePreset: 'custom',
                    })
                  }
                  className="w-full accent-brand-500"
                />
                <div className="mt-1 flex justify-between text-[10px] text-surface-400">
                  <span>1 km</span>
                  <span>49 km</span>
                </div>
              </div>
            )}
          </div>
        );
      case 'availability':
        return (
          <div className="flex flex-col gap-1.5">
            {[
              { id: 'any', label: 'All Vibes' },
              { id: 'hasSpots', label: 'Spots open' },
              { id: 'almostFull', label: 'Almost full' },
            ].map((o) => (
              <OptionRow
                key={o.id}
                selected={draft.availabilityMode === o.id}
                onClick={() => {
                  if (o.id !== 'any' && draft.availabilityMode === o.id) {
                    set({ availabilityMode: 'any' });
                  } else {
                    set({ availabilityMode: o.id });
                  }
                }}
              >
                {o.label}
              </OptionRow>
            ))}
            <label className="mt-1 flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-surface-100 bg-white px-3 py-2.5 dark:border-surface-800 dark:bg-surface-900">
              <span className="text-[13px] font-medium text-surface-600 dark:text-surface-300">Show full vibes</span>
              <input
                type="checkbox"
                checked={draft.showFullActivities}
                onChange={(e) => set({ showFullActivities: e.target.checked })}
                className="h-5 w-5 shrink-0 rounded border-surface-300 text-brand-600 focus:ring-brand-500"
              />
            </label>
          </div>
        );
      default:
        return null;
    }
  };

  const activeMeta = EXPLORE_FILTER_CATEGORY_ORDER.find((c) => c.id === activeCategory);

  return (
    <div className="fixed inset-0 z-[1200]">
      <button type="button" className="absolute inset-0 bg-black/35 backdrop-blur-[1px]" aria-label="Close filters" onClick={onClose} />
      <div
        className="absolute bottom-0 left-0 right-0 flex h-[min(88vh,640px)] max-h-[90vh] flex-col rounded-t-3xl border border-surface-200/60 bg-white shadow-lg shadow-surface-900/5 animate-slide-up dark:border-surface-800 dark:bg-surface-900 lg:bottom-auto lg:left-1/2 lg:top-1/2 lg:h-[min(86vh,620px)] lg:max-h-[90vh] lg:w-[min(100vw-2rem,480px)] lg:-translate-x-1/2 lg:-translate-y-1/2 lg:rounded-3xl lg:shadow-xl lg:animate-none"
        role="dialog"
        aria-modal="true"
        aria-labelledby="explore-filter-sheet-title"
      >
        <div className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-surface-300 dark:bg-surface-600 lg:hidden" />

        <div className="flex shrink-0 items-center justify-between border-b border-surface-100 px-4 py-2.5 dark:border-surface-800/80">
          <h2
            id="explore-filter-sheet-title"
            className="text-[15px] font-semibold tracking-tight text-surface-700 dark:text-surface-100"
          >
            Filters
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-surface-400 transition-colors hover:bg-surface-100 hover:text-surface-600 dark:hover:bg-surface-800 dark:hover:text-surface-300"
            aria-label="Close"
          >
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>

        {/* Two-column body: sidebar + options */}
        <div className="flex min-h-0 flex-1 divide-x divide-surface-100 dark:divide-surface-800/80">
          <nav
            className="w-[min(40%,10.5rem)] shrink-0 overflow-y-auto overscroll-contain bg-surface-50/90 dark:bg-surface-950/80"
            aria-label="Filter categories"
          >
            <ul className="py-0.5">
              {EXPLORE_FILTER_CATEGORY_ORDER.map((cat) => {
                const isActive = activeCategory === cat.id;
                return (
                  <li key={cat.id}>
                    <button
                      type="button"
                      onClick={() => setActiveCategory(cat.id)}
                      className={[
                        'w-full border-b border-surface-100/90 px-2.5 py-2.5 text-left text-[12px] font-medium leading-snug transition-colors dark:border-surface-800/60',
                        isActive
                          ? 'border-l-[3px] border-l-brand-400/70 bg-white text-surface-900 shadow-[1px_0_0_rgba(0,0,0,0.02)] dark:border-l-brand-400/50 dark:bg-surface-900 dark:text-white'
                          : 'border-l-[3px] border-l-transparent text-surface-500 hover:bg-white/60 hover:text-surface-700 dark:text-surface-400 dark:hover:bg-surface-900/60 dark:hover:text-surface-200',
                      ].join(' ')}
                    >
                      <span className="break-words">{cat.sidebarLabel}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div
            className="min-w-0 flex-1 overflow-y-auto overscroll-contain bg-white dark:bg-surface-900"
            role="tabpanel"
            aria-label={activeMeta ? `${activeMeta.sidebarLabel} options` : 'Filter options'}
          >
            <div className="p-3.5 sm:p-4">
              {activeMeta ? (
                <div className="mb-3 border-b border-surface-100 pb-3 dark:border-surface-800/80">
                  
                  {activeMeta.description ? (
                    <p className="mt-1.5 text-[12px] font-normal leading-relaxed text-surface-500 dark:text-surface-400">
                      {activeMeta.description}
                    </p>
                  ) : null}
                </div>
              ) : null}
              {renderOptions()}
            </div>
          </div>
        </div>

        <div className="shrink-0 border-t border-surface-100 bg-white/95 px-4 py-2.5 backdrop-blur-sm dark:border-surface-800/80 dark:bg-surface-950/95 safe-bottom">
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={handleReset}
              className="shrink-0 py-2 text-[13px] font-medium text-surface-500 hover:text-surface-800 dark:text-surface-400 dark:hover:text-surface-200"
            >
              Clear all
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="btn-primary min-h-[42px] min-w-[6.5rem] shrink-0 !px-5 !py-2 !text-[13px] !font-semibold shadow-sm shadow-brand-500/15"
            >
              Apply{draftCount > 0 ? ` (${draftCount})` : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExploreFilterSheet;
